import React, { useState, useEffect, useRef } from "react";
import { IonContent, IonText, IonButton, IonItem, IonLabel, IonModal } from "@ionic/react";
import { Helmet } from "react-helmet";

const InOut: React.FC = () => {
    const positions = ["In", "Out"];
    const [emojiPosition, setEmojiPosition] = useState("In");
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);
    const [selectedOption, setSelectedOption] = useState<string | null>(null);

    useEffect(() => {
        generateNewQuestion();
        document.title = "In and Out Worksheets for UKG | Spatial Concept Activities";
    }, []);

    const generateNewQuestion = () => {
        const randomPosition = positions[Math.floor(Math.random() * positions.length)];
        setEmojiPosition(randomPosition);
        setSelectedOption(null);
    };

    const handleAnswerSubmit = (answer: string) => {
        setSelectedOption(answer);
        if (answer === emojiPosition) {
            setShowImage(true);
            setTimeout(() => {
                generateNewQuestion();
                setShowImage(false);
            }, 3000);
        }
        else {
            setShowImage(false)
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content="Teach UKG children the concept of in and out using fun visual worksheets. Help kids build spatial awareness and position understanding through simple pre-math activities." />
                <link rel="canonical" href="https://mamasguidance.com/ukg/mathematics/pre-math-concepts/in-out" />
            </Helmet>
            {showImage === false ? (
                <div className="contentPart showCenter additionWord MoneyDiv">
                    <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                    <IonModal
                        ref={modal}
                        trigger="open-modal"
                        initialBreakpoint={1}
                        breakpoints={[0, 0.5, 0.75, 1]}
                        handleBehavior="cycle"
                        backdropDismiss={false}  // ✅ Prevents click-to-close
                        className="full-page-modal"
                    >
                        <IonContent className="ion-padding">
                            <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                            <h1>In and Out</h1>

                            <p>
                                The concept of <strong>in</strong> and <strong>out</strong> plays a crucial role in developing a child’s early spatial and mathematical understanding. With our specially designed <strong>UKG worksheets</strong>, children can easily learn how to recognize and use these position words in everyday contexts.
                            </p>

                            <h2>📦 What Do In and Out Mean?</h2>
                            <p>
                                <strong>In</strong> means something is inside or within something else, and <strong>Out</strong> means something is outside or beyond an area.
                                <ul>
                                    <li>A toy <strong>in</strong> the box</li>
                                    <li>A cat <strong>out</strong> of the basket</li>
                                    <li>Books <strong>in</strong> the bag</li>
                                    <li>Balls <strong>out</strong> of the bin</li>
                                </ul>
                            </p>

                            <h2>🎯 Learning Objectives</h2>
                            <ul>
                                <li>Identify objects that are “in” or “out” </li>
                                <li>Understand spatial relationships using real-world scenarios</li>
                                <li>Use position vocabulary in speaking and understanding</li>
                                <li>Build early logical and visual discrimination skills</li>
                            </ul>

                            <h2>🖍️ What’s Inside the Worksheets?</h2>
                            <ul>
                                <li>Circle the object that is in or out</li>
                                <li>Match objects based on their position</li>
                                <li>Color items that are inside a space</li>
                                <li>Sort images into “in” and “out” categories</li>
                                <li>Interactive scenes with animals or toys in and out of places</li>
                            </ul>

                            <h2>👩‍🏫 Tips for Parents & Teachers</h2>
                            <ul>
                                <li>Use baskets, boxes, and bags to show real “in” and “out” concepts</li>
                                <li>Play games like “put the ball in the bucket”</li>
                                <li>Tell stories and ask questions like “Where is the rabbit? In or out of the burrow?”</li>
                                <li>Encourage your child to describe the position of their toys and books</li>
                            </ul>


                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/ukg/mathematics/pre-math-concepts/long-short"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Long - Short</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/mathematics/pre-math-concepts/heavy-light"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Heavy - Light</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/mathematics/pre-math-concepts/front-back"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Front - Back</IonLabel>
                                </IonItem>
                            </div>

                            <h2>🌟 Mama’s Guidance Tip</h2>
                            <p>
                                Play sorting games with your child using toys or clothes. Ask, <strong>“Can you find the biggest teddy?” or “Which sock is smaller?”</strong> — simple activities go a long way!
                            </p>

                            <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                        </IonContent>
                    </IonModal>
                    <article className="visually-hidden">
                        <h1>In and Out</h1>

                        <p>
                            The concept of <strong>in</strong> and <strong>out</strong> plays a crucial role in developing a child’s early spatial and mathematical understanding. With our specially designed <strong>UKG worksheets</strong>, children can easily learn how to recognize and use these position words in everyday contexts.
                        </p>

                        <h2>📦 What Do In and Out Mean?</h2>
                        <p>
                            <strong>In</strong> means something is inside or within something else, and <strong>Out</strong> means something is outside or beyond an area.
                            <ul>
                                <li>A toy <strong>in</strong> the box</li>
                                <li>A cat <strong>out</strong> of the basket</li>
                                <li>Books <strong>in</strong> the bag</li>
                                <li>Balls <strong>out</strong> of the bin</li>
                            </ul>
                        </p>

                        <h2>🎯 Learning Objectives</h2>
                        <ul>
                            <li>Identify objects that are “in” or “out” </li>
                            <li>Understand spatial relationships using real-world scenarios</li>
                            <li>Use position vocabulary in speaking and understanding</li>
                            <li>Build early logical and visual discrimination skills</li>
                        </ul>

                        <h2>🖍️ What’s Inside the Worksheets?</h2>
                        <ul>
                            <li>Circle the object that is in or out</li>
                            <li>Match objects based on their position</li>
                            <li>Color items that are inside a space</li>
                            <li>Sort images into “in” and “out” categories</li>
                            <li>Interactive scenes with animals or toys in and out of places</li>
                        </ul>

                        <h2>👩‍🏫 Tips for Parents & Teachers</h2>
                        <ul>
                            <li>Use baskets, boxes, and bags to show real “in” and “out” concepts</li>
                            <li>Play games like “put the ball in the bucket”</li>
                            <li>Tell stories and ask questions like “Where is the rabbit? In or out of the burrow?”</li>
                            <li>Encourage your child to describe the position of their toys and books</li>
                        </ul>


                        <h2>🔗 Related Topics</h2>
                        <div>
                            <IonItem routerLink="/ukg/mathematics/pre-math-concepts/long-short"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Long - Short</IonLabel>
                            </IonItem>
                            <IonItem routerLink="/ukg/mathematics/pre-math-concepts/heavy-light"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Heavy - Light</IonLabel>
                            </IonItem>
                            <IonItem routerLink="/ukg/mathematics/pre-math-concepts/front-back"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Front - Back</IonLabel>
                            </IonItem>
                        </div>

                        <h2>🌟 Mama’s Guidance Tip</h2>
                        <p>
                            Play sorting games with your child using toys or clothes. Ask, <strong>“Can you find the biggest teddy?” or “Which sock is smaller?”</strong> — simple activities go a long way!
                        </p>
                    </article>
                    <div className="text-center">
                        <h3>Where is the 🎈?</h3>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginTop: "20px" }}>
                        <div style={{ position: "relative", width: "100px", height: "100px", border: "2px solid black", textAlign: "center", fontSize: "30px", lineHeight: "100px", marginBottom: "20px" }}>
                            {emojiPosition === "In" && "🎈"}
                        </div>
                        {emojiPosition === "Out" && <div style={{ fontSize: "30px" }}>🎈</div>}
                    </div>
                    <div style={{ display: "flex", gap: "10px", justifyContent: "center", marginTop: "20px" }}>
                        {positions.map((option) => (
                            <IonButton
                                key={option}
                                color={selectedOption === option ? (option === emojiPosition ? "success" : "danger") : "primary"}
                                onClick={() => handleAnswerSubmit(option)}
                            >
                                {option}
                            </IonButton>
                        ))}
                    </div>
                </div>
            ) : (
                <div className="contentPart showMiddle">
                    <img style={{ height: "150px" }} className="img-fluid" src={process.env.REACT_APP_WELDONE_LINK} alt="Well Done" />
                </div>
            )}
        </>
    );
};

export default InOut;
